import React, { useState } from 'react';
import {
  Home,
  FileText,
  Store,
  Users,
  Phone,
  Newspaper,
  LifeBuoy,
  Droplets,
  ClipboardList,
  Smile,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

type MenuItem = {
  name: string;
  icon?: React.ElementType;
  path?: string;
  children?: MenuItem[];
};

const menu: MenuItem[] = [
  { name: 'ダッシュボード', icon: Home, path: '/manager' },
  { name: '更新履歴', icon: FileText, path: '/manager/update-history' },
  {
    name: '店舗管理',
    icon: Store,
    children: [
      { name: '店舗管理', path: '/manager/member' },
      { name: '協力パートナー管理', path: '/manager/supporter' },
      { name: '契約情報管理', path: '/manager/contract' },
    ],
  },
  { name: 'ログインID管理', icon: Users, path: '/manager/login-id' },
  { name: 'コールグループ', icon: Phone, path: '/manager/callgroup' },
  {
    name: 'ニュース管理',
    icon: Newspaper,
    children: [
      { name: 'ニュースタイプ', path: '/manager/news-type' },
      { name: 'ニュース', path: '/manager/news' },
    ],
  },
  {
    name: 'レスキュー管理',
    icon: LifeBuoy,
    children: [
      { name: 'レスキュー項目管理', path: '/manager/rescue-items' },
      { name: '修理依頼履歴', path: '/manager/repair-history' },
      { name: 'レポート報告管理', path: '/manager/report-management' },
      { name: '解決アシスト', path: '/manager/assist' },
      { name: '連絡管理', path: '/manager/contact' },
      { name: '対応内容管理', path: '/manager/response' },
    ],
  },
  {
    name: '食洗機洗浄レポート管理',
    icon: Droplets,
    children: [
      { name: '食洗機洗浄レポート', path: '/manager/dw-report' },
      { name: 'セクション管理', path: '/manager/dw-section' },
      { name: 'レポート項目管理', path: '/manager/dw-items' },
      { name: '単位管理', path: '/manager/dw-unit' },
      { name: '条件管理', path: '/manager/dw-condition' },
      { name: 'テンプレート管理', path: '/manager/dw-template' },
    ],
  },
  {
    name: 'オリジナルレポート',
    icon: FileText,
    children: [
      { name: 'オリジナルレポート', path: '/manager/original-report' },
      { name: 'セクション管理', path: '/manager/original-section' },
      { name: 'レポート項目管理', path: '/manager/original-items' },
      { name: '条件管理', path: '/manager/original-condition' },
      { name: 'テンプレート管理', path: '/manager/original-template' },
    ],
  },
  {
    name: '発注書レポート管理',
    icon: ClipboardList,
    children: [
      { name: '発注書レポート', path: '/manager/order-report' },
      { name: 'セクション管理', path: '/manager/order-section' },
      { name: '商品項目', path: '/manager/order-items' },
      { name: 'テンプレート管理', path: '/manager/order-template' },
    ],
  },
  {
    name: '覆面・満足度調査レポート管理',
    icon: Smile,
    children: [
      { name: '覆面・満足度調査レポート', path: '/manager/survey-report' },
      { name: 'セクション管理', path: '/manager/survey-section' },
      { name: 'レポート項目管理', path: '/manager/survey-items' },
      { name: 'テンプレート管理', path: '/manager/survey-template' },
      { name: 'メニュー・商品管理', path: '/manager/survey-menu' },
    ],
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const isActive = (path?: string) => path && location.pathname.startsWith(path);

  return (
    <aside className="w-64 h-full bg-white border-r">
      <div className="p-4 text-xl font-bold text-center text-gray-800">Adminator</div>
      <nav>
        {menu.map((item) => (
          <div key={item.name}>
            {item.children ? (
              <>
                <button
                  type="button"
                  className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none"
                  onClick={() => toggleMenu(item.name)}
                >
                  {item.icon && <item.icon className="w-5 h-5 mr-3" />}
                  <span className="flex-1 text-left">{item.name}</span>
                  {openMenus[item.name] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                {openMenus[item.name] && (
                  <div className="border-l border-gray-200 ml-7">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.name}
                        to={child.path!}
                        className={({ isActive: isChildActive }) =>
                          `block px-4 py-2 text-gray-600 hover:bg-gray-50 text-sm border-l-2 ${
                            isChildActive || isActive(child.path)
                              ? 'border-primary bg-gray-100 font-semibold text-primary'
                              : 'border-transparent'
                          }`
                        }
                      >
                        {child.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path!}
                className={({ isActive: isTopActive }) =>
                  `flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 ${
                    isTopActive || isActive(item.path)
                      ? 'bg-gray-200 font-semibold text-primary'
                      : ''
                  }`
                }
              >
                {item.icon && <item.icon className="w-5 h-5 mr-3" />}
                {item.name}
              </NavLink>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
